import { useState, useEffect, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { HelpTip } from "../components/HelpTip";
import { API, useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Search, Sparkles, Layers3, Users, ShieldCheck, Building2 } from "lucide-react";

const MODULE_LABELS = {
  company: "Empresa",
  employees: "Empleados",
  attendance: "Asistencia",
  organigrama: "Organigrama",
  performance: "Desempeño",
  "knowledge-base": "Manual"
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin_empresa: "Admin Empresa",
  gerente_rh: "Gerente RH",
  supervisor: "Supervisor",
  empleado: "Colaborador"
};

const badgeByModule = {
  company: "bg-sky-100 text-sky-700",
  employees: "bg-emerald-100 text-emerald-700",
  attendance: "bg-rose-100 text-rose-700",
  organigrama: "bg-teal-100 text-teal-700",
  performance: "bg-violet-100 text-violet-700",
  "knowledge-base": "bg-amber-100 text-amber-700"
};

export default function KnowledgeBase() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");

  const opts = useCallback(() => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };
  }, []);

  const fetchArticles = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = { audience_role: user.role };
      if (searchTerm) params.search = searchTerm;
      if (moduleFilter !== "all") params.module = moduleFilter;
      const response = await axios.get(`${API}/knowledge-base`, { ...opts(), params });
      setArticles(response.data || []);
    } catch {
      toast.error("Error al cargar el manual");
    } finally {
      setLoading(false);
    }
  }, [moduleFilter, opts, searchTerm, user]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/knowledge-base/seed-defaults`, {}, opts());
      toast.success("Manual base generado");
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo generar el manual");
    } finally {
      setSeeding(false);
    }
  };

  const featuredArticles = useMemo(() => articles.filter(article => article.featured), [articles]);
  const availableModules = useMemo(() => [...new Set(articles.map(article => article.module).filter(Boolean))], [articles]);
  const canSeed = ["super_admin", "admin_empresa", "gerente_rh"].includes(user?.role);

  return (
    <Layout
      title="Manual Inteligente"
      subtitle="Guías operativas conectadas a tu estructura, líderes y centros de trabajo"
      actions={canSeed ? (
        <Button onClick={handleSeed} disabled={seeding} className="bg-sky-600 hover:bg-sky-700" data-testid="knowledge-seed-button">
          {seeding ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {seeding ? "Generando..." : "Generar manual base"}
        </Button>
      ) : null}
    >
      <div className="grid lg:grid-cols-[1.4fr_0.9fr] gap-6 mb-6">
        <Card className="border-slate-200 bg-gradient-to-br from-white via-sky-50/80 to-cyan-50/60">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-sky-600 text-white flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-slate-900">Ayuda contextual de verdad</h2>
                <p className="text-sm text-slate-600 max-w-2xl">
                  Este módulo concentra cómo operar centros de trabajo, asignar jefes inmediatos, leer el organigrama,
                  interpretar asistencia por sede y convertir desempeño en planes accionables.
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Badge className="bg-sky-100 text-sky-700">Multisede</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700">Por rol</Badge>
                  <Badge className="bg-violet-100 text-violet-700">Conectado a desempeño</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Users className="w-4 h-4 text-sky-600" />
              <p className="text-sm font-semibold">Vista activa para tu rol</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Perfil</p>
              <p className="text-lg font-bold text-slate-900">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
            <div className="flex items-start gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-500" />
              <p>Los artículos mostrados priorizan lo que realmente necesitas según tu rol actual.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por tema, proceso o módulo..."
              className="pl-10"
              data-testid="knowledge-search-input"
            />
          </div>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="w-[220px]" data-testid="knowledge-module-filter">
              <SelectValue placeholder="Filtrar por módulo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los módulos</SelectItem>
              {availableModules.map(module => (
                <SelectItem key={module} value={module}>{MODULE_LABELS[module] || module}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <HelpTip title="Cómo usar este manual">
            <p>Filtra por módulo y busca procesos específicos.</p>
            <p>Los artículos destacados están pensados para acelerar adopción y operación multisede.</p>
          </HelpTip>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Tabs defaultValue="featured" className="space-y-4">
          <TabsList className="flex-wrap h-auto gap-1" data-testid="knowledge-tabs">
            <TabsTrigger value="featured" data-testid="knowledge-tab-featured">Destacados</TabsTrigger>
            <TabsTrigger value="all" data-testid="knowledge-tab-all">Todos</TabsTrigger>
          </TabsList>

          <TabsContent value="featured">
            {featuredArticles.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500" data-testid="knowledge-featured-empty">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Aún no hay artículos destacados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-2 gap-4" data-testid="knowledge-featured-grid">
                {featuredArticles.map(article => (
                  <Card key={article.article_id} className="border-slate-200 hover:shadow-md transition-all">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <CardTitle className="text-lg text-slate-900">{article.title}</CardTitle>
                          <p className="text-sm text-slate-500 mt-1">{article.summary || "Guía operativa"}</p>
                        </div>
                        <Badge className={badgeByModule[article.module] || "bg-slate-100 text-slate-700"}>
                          {MODULE_LABELS[article.module] || article.module}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm leading-6 text-slate-700 whitespace-pre-line">{article.content}</p>
                      <div className="flex flex-wrap gap-2">
                        {(article.audience_roles || []).map(role => (
                          <Badge key={role} variant="outline">{ROLE_LABELS[role] || role}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all">
            {articles.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500" data-testid="knowledge-empty-state">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No hay artículos para tu empresa todavía</p>
                  <p className="text-sm mt-1">Genera el manual base para empezar con centros, jefes inmediatos y desempeño.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="knowledge-articles-grid">
                {articles.map(article => (
                  <Card key={article.article_id} className="border-slate-200 h-full">
                    <CardContent className="p-5 h-full flex flex-col">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <Badge className={badgeByModule[article.module] || "bg-slate-100 text-slate-700"}>
                          {MODULE_LABELS[article.module] || article.module}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Layers3 className="w-3.5 h-3.5" />
                          {article.category}
                        </div>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-2">{article.title}</h3>
                      <p className="text-sm text-slate-600 mb-4">{article.summary || "Guía de operación"}</p>
                      <p className="text-sm text-slate-700 leading-6 whitespace-pre-line flex-1">{article.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </Layout>
  );
}